import { CloseCircleOutlined, FilterOutlined } from '@ant-design/icons';
import {
  Button,
  DatePicker,
  Form,
  Skeleton,
  Space,
  Table,
  Typography,
} from 'antd';

import {
  CustomAlert,
  Error403Unauthorized,
  MultipleSelect,
} from '@app/components';
import {
  DATE_PICKER_FORMAT,
  DIRECTION_INDICATORS,
  DIRECTION_INDICATORS_HEADER,
  ERROR,
  fistDayOfCurrentMonth,
  OFF,
  PREFIX,
  rangePickerLocale,
  U_KEY,
} from '@app/constants';

import { useDirectionIndicators } from '@pages/Analytics/DirectionsIndicatorsList/hooks/useDirectionIndicators';

import { adaptDataToTable } from '@pages/Analytics/DirectionsIndicatorsList/utils/adaptDataToTable';

import type { FC } from 'react';

import '@pages/Analytics/DirectionsIndicatorsList/styles/DirectionIndicators.desktop.scss';
import '@pages/Analytics/DirectionsIndicatorsList/styles/DirectionIndicators.tablet.scss';
import '@pages/Analytics/DirectionsIndicatorsList/styles/DirectionIndicators.mobile.scss';

const { Text } = Typography;
const { RangePicker } = DatePicker;

const HEADER_HEIGHT = 480;

export const DirectionIndicators: FC = () => {
  const {
    isFiltersLoading,
    form,
    today,
    searchError,
    setSearchError,
    selectedEmployees,
    setSelectedEmployees,
    selectedUsers,
    setSelectedUsers,
    selectedClientServices,
    setSelectedClientServices,
    selectedClientServiceCategories,
    setSelectedClientServiceCategories,
    analyticsServiceCategories,
    handleReset,
    submitForm,
    hasUserRightForModuleView,
    isGetAnalyticsServiceCategoriesLoading,
    employeesSelectOptions,
    userSelectOptions,
    clientServiceCategoriesSelectOptions,
    clientServiceSelectOptions,
  } = useDirectionIndicators();

  if (!hasUserRightForModuleView) {
    return <Error403Unauthorized />;
  }

  if (isFiltersLoading) {
    return <Skeleton active />;
  }

  return (
    <div className={`${PREFIX}${DIRECTION_INDICATORS}`}>
      <div className="header-container">
        <Form
          form={form}
          autoComplete={OFF}
          onFinish={(values) => submitForm(values)}
          labelCol={{ span: 4 }}
          wrapperCol={{ span: 30 }}
          style={{ marginBottom: 10 }}
          layout="vertical"
          initialValues={{
            Date: [fistDayOfCurrentMonth, today],
          }}
        >
          <div className="date-container">
            <div className="date-item">
              <Text className="label" strong>
                Период
              </Text>
              <Form.Item name="Date">
                <RangePicker
                  format={DATE_PICKER_FORMAT}
                  size="middle"
                  allowEmpty={[true, true]}
                  locale={rangePickerLocale}
                />
              </Form.Item>
            </div>
          </div>

          <div className="selectors-container">
            <div className="selectors">
              <MultipleSelect
                className="user-selector"
                placeholder="Все исполнители"
                allOptions={employeesSelectOptions}
                width={100}
                selectedOption={selectedEmployees}
                setSelectedOptions={setSelectedEmployees}
                filterName="filterName"
              />

              <MultipleSelect
                className="user-selector"
                placeholder="Все пользователи"
                allOptions={userSelectOptions}
                width={100}
                selectedOption={selectedUsers}
                setSelectedOptions={setSelectedUsers}
                filterName="filterName"
              />

              <MultipleSelect
                className="user-selector"
                placeholder="Все категории услуг"
                allOptions={clientServiceCategoriesSelectOptions}
                width={100}
                selectedOption={selectedClientServiceCategories}
                setSelectedOptions={setSelectedClientServiceCategories}
                filterName="filterName"
              />

              <MultipleSelect
                className="user-selector"
                placeholder="Все услуги"
                allOptions={clientServiceSelectOptions}
                width={100}
                selectedOption={selectedClientServices}
                setSelectedOptions={setSelectedClientServices}
                filterName="filterName"
              />

              <Form.Item
                style={{
                  display: 'inline-block',
                  marginBottom: 0,
                }}
                className="buttons-container"
              >
                {searchError && (
                  <CustomAlert
                    errorType={ERROR}
                    serverError={searchError}
                    setServerError={setSearchError}
                  />
                )}
                <Space className="tablet-buttons" size="middle">
                  <Button
                    htmlType="reset"
                    onClick={handleReset}
                    className="button"
                  >
                    <CloseCircleOutlined />
                    Сброс
                  </Button>

                  <Button type="primary" htmlType="submit" className="button">
                    <FilterOutlined />
                    Показать
                  </Button>
                </Space>
              </Form.Item>
            </div>

            <Form.Item
              style={{
                display: 'inline-block',
                marginBottom: 0,
              }}
              className="buttons-container"
            >
              {searchError && (
                <CustomAlert
                  errorType={ERROR}
                  serverError={searchError}
                  setServerError={setSearchError}
                />
              )}
              <Space className="desktop-buttons" size="middle">
                <Button
                  htmlType="reset"
                  onClick={handleReset}
                  className="button"
                >
                  <CloseCircleOutlined />
                  Сброс
                </Button>

                <Button type="primary" htmlType="submit" className="button">
                  <FilterOutlined />
                  Показать
                </Button>
              </Space>
            </Form.Item>
          </div>
        </Form>
      </div>

      <Table
        className="clients-payments-table"
        columns={DIRECTION_INDICATORS_HEADER}
        loading={isGetAnalyticsServiceCategoriesLoading}
        bordered
        dataSource={adaptDataToTable(analyticsServiceCategories)}
        rowKey={U_KEY}
        pagination={false}
        scroll={{ x: 'max-content', y: `calc(100vh - ${HEADER_HEIGHT}px)` }}
      />
    </div>
  );
};
